import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Loader, Upload } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { sportTypeAPI } from '../../services/api';
import { useSquads } from '../hooks/useSquads';
import { moveImage } from '../../services/imageUploadService';
import ImageCropModal, { ALLOWED_IMAGE_TYPES } from '../../components/common/ImageCropModal';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import { btn } from '../../utils/buttonStyles';
import { useImagePicker } from '../../hooks/useImagePicker.jsx';

/**
 * SquadCreateEdit - Self-contained Squad Create/Edit Modal
 *
 * Props:
 * - isOpen: Boolean to show/hide modal
 * - onClose: Callback when modal is closed
 * - onSuccess: Callback when squad is created/updated successfully (squad) => void
 * - squad: Squad object for editing (null for create mode)
 * - mode: 'create' or 'edit'
 * - fixedSportTypeId: A program's class plays the program's sport. When given,
 *   a new squad starts with it and the game type cannot be changed.
 */
const SquadCreateEdit = ({
  isOpen,
  onClose,
  onSuccess,
  squad = null,
  mode = 'create',
  fixedSportTypeId = null
}) => {
  const { theme } = useTheme();
  const isEditMode = mode === 'edit' && squad !== null;

  // Use the useSquads hook for API calls
  const { createGroup, updateGroup } = useSquads(false);

  const GROUP_DESCRIPTION_MAX_LENGTH = parseInt(import.meta.env.REACT_APP_GROUP_DESCRIPTION_MAX_LENGTH) || 4000;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy: 'public',
    sportTypeId: '',
    backgroundImage: ''
  });
  const [sportTypes, setSportTypes] = useState([]);
  const [sportTypesLoading, setSportTypesLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  // Background image state
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);
  // Pending image URL from temp upload — moderation already passed at crop time
  const [pendingImageUrl, setPendingImageUrl] = useState(null);

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);

  // Load sport types on mount
  useEffect(() => {
    const loadSportTypes = async () => {
      try {
        const response = await sportTypeAPI.getAll();
        setSportTypes(response?.data || response || []);
      } catch (error) {
        console.error('Failed to load sport types:', error);
      } finally {
        setSportTypesLoading(false);
      }
    };
    loadSportTypes();
  }, []);

  // Populate form data when editing
  useEffect(() => {
    const populateFormData = async () => {
      if (isEditMode && squad) {
        setFormData({
          name: squad.name || '',
          description: squad.description || '',
          privacy: squad.privacy || 'public',
          sportTypeId: squad.sportTypeId || '',
          backgroundImage: squad.backgroundImage || ''
        });

        // backgroundImage stored as /images/group_assets/{id}/images/background.jpeg —
        // use directly (Vite proxies /images/* to backend in dev, CloudFront in prod)
        if (squad.backgroundImage) {
          setBackgroundImagePreview(resolveImageUrl(squad.backgroundImage));
        }
      } else {
        // Reset form for create mode
        setFormData({
          name: '',
          description: '',
          privacy: 'public',
          sportTypeId: fixedSportTypeId ? String(fixedSportTypeId) : '',
          backgroundImage: ''
        });
        setPendingImageUrl(null);
        setBackgroundImagePreview(null);
      }
    };

    if (isOpen) {
      populateFormData();
    }
  }, [isEditMode, squad, isOpen, fixedSportTypeId]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const { openPicker, pickerInput } = useImagePicker({
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    onFiles: (files) => {
      const file = files[0]
      if (!file) return
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size exceeds 10MB limit.')
        return
      }
      const reader = new FileReader()
      reader.onloadend = () => {
        setSelectedImageForCrop(reader.result)
        setCropModalOpen(true)
      }
      reader.readAsDataURL(file)
    },
  });

  // Called only on moderation success, with the temp upload result { url, key }
  const handleCroppedImage = async ({ url }) => {
    setBackgroundImagePreview(resolveImageUrl(url));
    if (isEditMode && squad?.id) {
      // Move immediately to canonical path since we have the entity ID
      const { url: canonicalUrl } = await moveImage(url, `group_assets/${squad.id}/images/${crypto.randomUUID()}.jpeg`);
      setFormData(prev => ({ ...prev, backgroundImage: canonicalUrl }));
    } else {
      setPendingImageUrl(url);
    }
  };

  const handleRemoveBackgroundImage = () => {
    setPendingImageUrl(null);
    setBackgroundImagePreview(null);
    setFormData(prev => ({ ...prev, backgroundImage: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter a squad name');
      return;
    }

    if (!formData.sportTypeId) {
      toast.error('Please select Game Type');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        privacy: formData.privacy,
        backgroundImage: formData.backgroundImage || ''
      };

      if (formData.sportTypeId && formData.sportTypeId !== '') {
        payload.sportTypeId = parseInt(formData.sportTypeId, 10);
      }

      let result;
      if (isEditMode) {
        result = await updateGroup(squad.id, payload);
        if (result.success || result.data || result.id) {
          toast.success('Squad updated successfully!');
          if (onSuccess) onSuccess(result.data || result);
          handleClose();
        } else {
          throw new Error(result.error || 'Failed to update squad');
        }
      } else {
        result = await createGroup(payload);
        if (!(result.success || result.data || result.id)) {
          throw new Error(result.error || 'Failed to create squad');
        }
        const newSquad = result.data || result;
        const newSquadId = newSquad.id;

        // Move temp upload to canonical path now that we have the squad ID
        if (pendingImageUrl && newSquadId) {
          const { url: canonicalUrl } = await moveImage(
            pendingImageUrl,
            `group_assets/${newSquadId}/images/${crypto.randomUUID()}.jpeg`
          );
          await updateGroup(newSquadId, { backgroundImage: canonicalUrl });
        }

        toast.success('Squad created successfully!');
        if (onSuccess) onSuccess(newSquad);
        handleClose();
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} squad:`, error);
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} squad`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        privacy: 'public',
        sportTypeId: '',
        backgroundImage: ''
      });
      setPendingImageUrl(null);
      setBackgroundImagePreview(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className={`${theme.card} rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto my-auto`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-bold ${theme.text}`}>
            {isEditMode ? 'Edit Squad' : 'Create New Squad'}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className={`p-1 rounded ${theme.cardHover} disabled:opacity-50`}
          >
            <X className={`w-5 h-5 ${theme.textMuted}`} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Squad Name */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              Squad Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
              placeholder="Enter squad name"
              maxLength={100}
              required
              disabled={loading}
            />
          </div>

          {/* Description */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
              placeholder="Describe your squad"
              rows={3}
              maxLength={GROUP_DESCRIPTION_MAX_LENGTH}
              disabled={loading}
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {formData.description.length} / {GROUP_DESCRIPTION_MAX_LENGTH}
            </div>
          </div>

          {/* Privacy */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              Privacy
            </label>
            <select
              value={formData.privacy}
              onChange={(e) => handleChange('privacy', e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
              disabled={loading}
            >
              <option value="public">Public - Anyone can join</option>
              <option value="private">Private - Invite only</option>
            </select>
          </div>

          {/* Sport Type */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              Game Type *
            </label>
            <select
              value={formData.sportTypeId}
              onChange={(e) => handleChange('sportTypeId', e.target.value)}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${fixedSportTypeId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              disabled={loading || sportTypesLoading || Boolean(fixedSportTypeId)}
              required
            >
              <option value="">Select a game type...</option>
              {sportTypes.map(sport => (
                <option key={sport.id} value={sport.id}>{sport.sportName}</option>
              ))}
            </select>
            {fixedSportTypeId && (
              <p className={`text-xs ${theme.textMuted} mt-1`}>Set by the program</p>
            )}
          </div>

          {/* Background Image Upload */}
          <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                Background Image
              </label>
              {backgroundImagePreview ? (
                <div className="relative">
                  <img
                    src={backgroundImagePreview}
                    alt="Background preview"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveBackgroundImage}
                    disabled={loading}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  role="button"
                  onClick={loading ? undefined : openPicker}
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed ${theme.border} rounded-lg cursor-pointer hover:${theme.backgroundSecondary} transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className={`w-8 h-8 ${theme.textSecondary} mb-2`} />
                    <p className={`text-sm ${theme.textSecondary}`}>
                      Click to upload background image
                    </p>
                    <p className={`text-xs ${theme.textMuted} mt-1`}>
                      JPEG, PNG, GIF, or WebP (max 10MB)
                    </p>
                  </div>
                  {pickerInput}
                </div>
              )}
            </div>

          {/* Actions */}
          <div className="flex gap-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex-1 disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1 disabled:opacity-50 flex items-center justify-center`}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{isEditMode ? 'Update Squad' : 'Create Squad'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    <ImageCropModal
      isOpen={cropModalOpen}
      onClose={() => { setCropModalOpen(false); setSelectedImageForCrop(null); }}
      imageSrc={selectedImageForCrop}
      fileType="group_assets"
      onCropComplete={handleCroppedImage}
      shape="rect"
      aspectRatio={3 / 1}
    />
    </>
  );
};

export default SquadCreateEdit;
